// libs
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { createRoot, Root } from 'react-dom/client'
import showdown from 'showdown'

// interfaces / constants

// constants
import { CLASS_PREFIX } from 'src/constants/'
import { dropUnsafeTargets } from 'src/utils/safe_url/safe_url'

// classes
import PreviewWithConsent from 'src/components/gdpr/preview_with_consent/preview_with_consent'

// styles
import {
  ImageShortcode,
  Shortcode,
  ShortcodeType,
  YoutubeShortcode
} from '@lopo-tech/lopo-api'
import './markdown_renderer.scss'

export const cls = `${CLASS_PREFIX}markdown-renderer`

const showdownOptions: showdown.ConverterOptions = {
  noHeaderId: true,
  openLinksInNewWindow: true,
  simplifiedAutoLink: true,
  tables: true
}

const showdownConverter = new showdown.Converter(showdownOptions)

export type IProps = {
  shortcodes: Shortcode[];
  text?: string | null;
};

const parseMarkdownWithShortCodes = (
  content: string,
  shortcodes: Shortcode[],
  clsShortcodePlaceholder: string
): string => {
  // Checked before the shortcode placeholders go in. That ordering is not what
  // protects them today: dropUnsafeTargets strips no tags of its own, so they
  // come through the parser unchanged either way, measured.
  //
  // It does lose certain elements sitting at the very start of the fragment - a
  // documented property of parsing one as a document, enumerated where the
  // function lives rather than repeated here, because one of them behaves
  // differently in the two parsers this code meets. That case is reachable from
  // here: measured, a body opening with such a block is emitted by the converter
  // with the block still first. The same element further in survives.
  //
  // The ordering becomes load-bearing the moment anyone filters tags or
  // attributes here - and at that point nothing would fail. The placeholders
  // would simply stop being found, and every image and video in every markdown
  // post would go missing in silence. Keep the checking in front of the
  // inserting.
  const converted: string = dropUnsafeTargets(showdownConverter.makeHtml(content))
  if (!shortcodes.length) {
    return converted
  }

  return converted.replace(/{{[\w\d_-]+}}/g, (match: string) => {
    const shortcode = shortcodes.find((sc) => sc.placeholder === match)
    if (!shortcode) {
      return match
    }

    return `<span class='${clsShortcodePlaceholder}' data-shortcode-id='${shortcode.id}'></span>`
  })
}

const parseContent = (
  content: string,
  shortcodes: Shortcode[],
  clsShortcodePlaceholder: string
): string => parseMarkdownWithShortCodes(content, shortcodes, clsShortcodePlaceholder)

// TODO: replace post ingredient with this component
/**
 * This markdown component should replace the old ingredient implementation long term.
 */
export const MarkdownRenderer: React.FC<IProps> = ({ shortcodes, text }) => {
  const bodyMarkdown = useRef<HTMLDivElement>(null)
  const shortcodeRoots = useRef<Map<HTMLElement, Root>>(new Map())

  const clsShortcodePlaceholder = useMemo(
    () => `${cls}__shortcode-placeholder`,
    [cls]
  )

  const stopNavigation = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
  }, [])

  const cleanupShortcodeRoots = useCallback((): void => {
    shortcodeRoots.current.forEach((root) => {
      root.unmount()
    })
    shortcodeRoots.current.clear()
  }, [])

  const renderShortcode = useCallback(
    (shortcode: Shortcode): React.ReactNode => {
      switch (shortcode.type) {
        case ShortcodeType.Image: {
          const { asset, id } = shortcode as ImageShortcode
          if (!asset) {
            return null
          }

          return (
            <figure
              className={`${cls}__image`}
              data-shortcode-id={id}
              onClick={stopNavigation}
            >
              <img src={asset.file.previewUrl} alt={asset.altText || ''} />
              {asset.name && (
                <figcaption className={`${cls}__image-caption`}>
                  {asset.name}
                </figcaption>
              )}
              {asset.source && (
                <p className={`${cls}__image-source`}>{asset.source}</p>
              )}
            </figure>
          )
        }
        case ShortcodeType.Youtube: {
          const { id, videoId } = shortcode as YoutubeShortcode
          if (!videoId) {
            return null
          }

          return (
            <div
              className={`${cls}__video`}
              data-shortcode-id={id}
              onClick={stopNavigation}
            >
              <PreviewWithConsent type="video" source="youtube">
                <div className={`${cls}__video-container`}>
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
                    allowFullScreen
                    title="YouTube video player"
                    className={`${cls}__video-frame`}
                  />
                </div>
              </PreviewWithConsent>
            </div>
          )
        }
        default:
          return null
      }
    },
    [cls, stopNavigation]
  )

  const html = useMemo((): string => {
    if (!text) {
      return ''
    }

    return parseContent(text, shortcodes, clsShortcodePlaceholder)
  }, [clsShortcodePlaceholder, shortcodes, text])

  useLayoutEffect(() => {
    cleanupShortcodeRoots()

    const container = bodyMarkdown.current
    if (!container) {
      return
    }

    const placeholders = container.querySelectorAll<HTMLElement>(
      `.${clsShortcodePlaceholder}`
    )

    placeholders.forEach((placeholder) => {
      const shortcodeId = placeholder.dataset.shortcodeId
      if (!shortcodeId) {
        return
      }

      const shortcode = shortcodes.find((sc) => sc.id === shortcodeId)
      if (!shortcode) {
        return
      }

      const content = renderShortcode(shortcode)
      if (!content) {
        return
      }

      let root = shortcodeRoots.current.get(placeholder)
      if (!root) {
        root = createRoot(placeholder)
        shortcodeRoots.current.set(placeholder, root)
      }

      root.render(content)
    })

    return () => {
      cleanupShortcodeRoots()
    }
  }, [cleanupShortcodeRoots, clsShortcodePlaceholder, html, renderShortcode, shortcodes])

  if (!text) {
    return null
  }

  return (
    <div className={cls} ref={bodyMarkdown}>
      <div
        dangerouslySetInnerHTML={{
          __html: html
        }}
      />
    </div>
  )
}
